import { FC, useState } from 'react'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@wellesley/ui'
import { ChevronUp } from 'lucide-react'
import { UserAvatar } from '@wellesley/user'

export type AuthorIdentityOption = {
  title: string
  id: string
  avatar?: string
  description: string
}

type AuthorIdentitySelectorProps = {
  options: AuthorIdentityOption[]
  selectedValue?: string | null
  onValueChange: (value: string | null) => void
}

const AuthorIdentitySelector: FC<AuthorIdentitySelectorProps> = ({
  options,
  selectedValue,
  onValueChange,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const selectedOption =
    options.find((opt) => opt.id === selectedValue) || options[0]

  const handleValueChange = (value: string) => {
    onValueChange(value)
  }

  return (
    <Select
      value={selectedValue || selectedOption.id}
      onValueChange={handleValueChange}
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <SelectTrigger
        data-testid="author-identity-trigger"
        className="gap-xxs rounded-m p-xxs flex h-auto items-center border-0 bg-gray-100 focus:outline-none"
        renderCaret={() => null}
      >
        <div className="gap-xxs flex items-center">
          <UserAvatar
            name={selectedOption.title}
            imgUrl={selectedOption.avatar}
            size={24}
          />
          <div className="gap-xxxs flex items-center">
            <span className="text-base text-gray-500">from</span>
            <SelectValue>
              <span className="text-base font-medium text-gray-900">
                {selectedOption.title}
              </span>
            </SelectValue>
          </div>
          <ChevronUp
            className={cn(
              'size-xxs shrink-0 transition-transform',
              isOpen && 'rotate-180',
            )}
          />
        </div>
      </SelectTrigger>
      <SelectContent
        align="start"
        data-testid="author-identity-menu"
        className="min-w-[240px]"
      >
        <SelectGroup>
          {options.map((option) => {
            const itemValue = option.id
            return (
              <SelectItem
                key={itemValue}
                value={itemValue}
                className="gap-xxs p-xxs flex items-center"
              >
                <div className="gap-xxs flex items-center">
                  <UserAvatar
                    name={option.title}
                    imgUrl={option.avatar}
                    size={40}
                  />
                  <div className="flex flex-1 flex-col">
                    <span className="text-base font-medium text-gray-900">
                      {option.title}
                    </span>
                    <span className="text-base font-normal text-gray-500">
                      {option.description}
                    </span>
                  </div>
                </div>
              </SelectItem>
            )
          })}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}

export { AuthorIdentitySelector }
export type { AuthorIdentitySelectorProps }
